/**
 * Les essentiels du groupe : ce que l'animateur doit savoir de chaque enfant
 * sans ouvrir sa fiche (décidé le 25/10/2026).
 *
 * La base rend les données brutes (`presences_lien_essentiels`) ; ici, on
 * décide ce qui mérite d'être montré. Les familles écrivent beaucoup de
 * « tout va bien » et de « RAS » dans les champs libres : la fiche santé PDF
 * les écarte déjà avec une liste de phrases (`isNoIssueText` dans
 * `supabase/functions/generate-subgroup-health-pdf/index.ts`). La même liste
 * est reprise ici, mot pour mot, pour que l'onglet « À savoir » et le PDF
 * disent la même chose. Si l'une bouge, l'autre doit bouger.
 */

export interface EssentielsDeLEnfant {
  registrationId: string;
  /** Le chemin de la photo dans l'espace `kid-photos`, ou `null`. L'adresse signée vient à part. */
  photo: string | null;
  /** La famille a dit oui pour l'espace des familles. Non, ou pas de réponse : pas de photo. */
  imageOk: boolean;
  sante: {
    allergies: boolean | null;
    allergiesDetails: string | null;
    allergiesConsequences: string | null;
    regime: boolean | null;
    regimeDetails: string | null;
    medicaments: boolean | null;
    medicamentsDetails: string | null;
    medicamentsAutonomie: boolean | null;
    aSavoir: string | null;
  };
  depart: {
    partSeul: boolean | null;
    heure: string | null;
    personnes: { prenom: string; nom: string }[];
  };
}

export type LectureDesEssentiels =
  | { codeRequis: true }
  | { codeRequis: false; parInscription: Map<string, EssentielsDeLEnfant> };

/* ------------------------------------------------------------------------ */
/*  Le tri des « tout va bien »                                              */
/* ------------------------------------------------------------------------ */

/* La liste du PDF (`isNoIssueText`), à l'identique. */
const PHRASES_SANS_IMPORTANCE = new Set([
  'tout va bien', 'tout est ok', 'tout est bon', 'tout ok', 'ok',
  'rien a signaler', 'rien de particulier', 'rien de special', 'rien',
  'ras', 'r a s', 'neant', 'aucun', 'aucune', 'aucun probleme',
  'pas de probleme', 'pas de soucis', 'pas de souci', 'pas de traitement',
  'non', 'n a', 'na', 'nc', 'non concerne', 'non concernee',
  'sans objet', 'sans', 'vide', 'x', '-', 'no', 'none',
]);

/** Vrai pour un champ vide, ou qui ne dit que « tout va bien » sous une forme ou une autre. */
export function estSansImportance(texte: string | null | undefined): boolean {
  if (!texte) return true;
  const normalise = texte
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[.,;:!?\-_/\\'"()]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (normalise === '') return true;
  return PHRASES_SANS_IMPORTANCE.has(normalise);
}

/* ------------------------------------------------------------------------ */
/*  Lecture de ce que rend la base                                           */
/* ------------------------------------------------------------------------ */

const texte = (v: unknown): string | null => {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  return t ? t : null;
};
const bool = (v: unknown): boolean | null => (typeof v === 'boolean' ? v : null);

/** Ce que rend `presences_lien_essentiels`. */
export function lireEssentiels(brut: unknown): LectureDesEssentiels {
  const r = (brut ?? {}) as { code_requis?: boolean; enfants?: unknown[] };
  if (r.code_requis || !Array.isArray(r.enfants)) return { codeRequis: true };
  const parInscription = new Map<string, EssentielsDeLEnfant>();
  for (const brutEnfant of r.enfants) {
    const e = (brutEnfant ?? {}) as Record<string, unknown>;
    const sante = (e.sante ?? {}) as Record<string, unknown>;
    const depart = (e.depart ?? {}) as Record<string, unknown>;
    const id = texte(e.registration_id);
    if (!id) continue;
    parInscription.set(id, {
      registrationId: id,
      photo: texte(e.photo),
      imageOk: e.image_ok === true,
      sante: {
        allergies: bool(sante.allergies),
        allergiesDetails: texte(sante.allergies_details),
        allergiesConsequences: texte(sante.allergies_consequences),
        regime: bool(sante.regime),
        regimeDetails: texte(sante.regime_details),
        medicaments: bool(sante.medicaments),
        medicamentsDetails: texte(sante.medicaments_details),
        medicamentsAutonomie: bool(sante.medicaments_autonomie),
        aSavoir: texte(sante.a_savoir),
      },
      depart: {
        partSeul: bool(depart.part_seul),
        heure: texte(depart.heure),
        personnes: (Array.isArray(depart.personnes) ? depart.personnes : []).flatMap((p) => {
          const q = (p ?? {}) as Record<string, unknown>;
          const prenom = texte(q.prenom) ?? '';
          const nom = texte(q.nom) ?? '';
          return prenom || nom ? [{ prenom, nom }] : [];
        }),
      },
    });
  }
  return { codeRequis: false, parInscription };
}

/** Ce que rend la fonction Edge `photos-presences` : l'adresse signée par inscription. */
export function lirePhotos(brut: unknown): Map<string, string> {
  const r = (brut ?? {}) as { photos?: Record<string, unknown> };
  const photos = new Map<string, string>();
  for (const [id, url] of Object.entries(r.photos ?? {})) {
    if (typeof url === 'string' && url) photos.set(id, url);
  }
  return photos;
}

/* ------------------------------------------------------------------------ */
/*  Ce qui se montre                                                         */
/* ------------------------------------------------------------------------ */

export type CleASavoir = 'allergies' | 'regime' | 'medicaments' | 'a_savoir' | 'depart' | 'photo';

export interface LigneASavoir {
  cle: CleASavoir;
  libelle: string;
  texte: string;
  /** À lire en premier : un danger ou une interdiction, pas une information. */
  alerte: boolean;
}

/** « Oui » quand la case est cochée sans détail utile ; le détail sinon ; rien si tout va bien. */
function detail(coche: boolean | null, texteLibre: string | null): string | null {
  if (!estSansImportance(texteLibre)) return texteLibre;
  return coche === true ? 'oui' : null;
}

/** « avec Papy Dupont ou Mamy Rossi ». */
export function personnesEnClair(personnes: readonly { prenom: string; nom: string }[]): string {
  return personnes.map((p) => `${p.prenom} ${p.nom}`.trim()).join(' ou ');
}

/** Les lignes à montrer pour un enfant, dans l'ordre de lecture ; vide quand rien n'est à signaler. */
export function lignesASavoir(e: EssentielsDeLEnfant): LigneASavoir[] {
  const lignes: LigneASavoir[] = [];
  const { sante, depart } = e;

  const allergies = detail(sante.allergies, sante.allergiesDetails);
  if (allergies) {
    const consequences = estSansImportance(sante.allergiesConsequences) ? '' : ` — ${sante.allergiesConsequences}`;
    lignes.push({ cle: 'allergies', libelle: 'Allergies', texte: `${allergies}${consequences}`, alerte: true });
  }
  const regime = detail(sante.regime, sante.regimeDetails);
  if (regime) lignes.push({ cle: 'regime', libelle: 'Régime', texte: regime, alerte: false });

  const medicaments = detail(sante.medicaments, sante.medicamentsDetails);
  if (medicaments) {
    const autonomie = sante.medicamentsAutonomie === true ? ' (les prend seul·e)' : '';
    lignes.push({ cle: 'medicaments', libelle: 'Médicaments', texte: `${medicaments}${autonomie}`, alerte: true });
  }
  if (!estSansImportance(sante.aSavoir)) {
    lignes.push({ cle: 'a_savoir', libelle: 'À savoir', texte: sante.aSavoir as string, alerte: false });
  }

  if (depart.partSeul === true) {
    lignes.push({
      cle: 'depart',
      libelle: 'Départ',
      texte: `Repart seul·e${depart.heure ? ` à ${depart.heure}` : ''}`,
      alerte: true,
    });
  } else if (depart.personnes.length > 0 || depart.heure) {
    const morceaux = [
      depart.heure ? `à ${depart.heure}` : null,
      depart.personnes.length > 0 ? `avec ${personnesEnClair(depart.personnes)}` : null,
    ].filter((m): m is string => m !== null);
    lignes.push({ cle: 'depart', libelle: 'Départ', texte: morceaux.join(' · '), alerte: false });
  }

  if (!e.imageOk) lignes.push({ cle: 'photo', libelle: 'Photo', texte: 'Pas de photo', alerte: true });

  return lignes;
}

export interface Indicateurs {
  /** Une allergie, un régime, un médicament ou un « à savoir » : le point rouge de la liste d'appel. */
  sante: boolean;
  /** La famille n'a pas dit oui pour l'espace des familles : l'appareil barré. */
  pasDePhoto: boolean;
}

export function indicateurs(e: EssentielsDeLEnfant | undefined): Indicateurs {
  if (!e) return { sante: false, pasDePhoto: false };
  const lignes = lignesASavoir(e);
  return {
    sante: lignes.some((l) => l.cle === 'allergies' || l.cle === 'regime' || l.cle === 'medicaments' || l.cle === 'a_savoir'),
    pasDePhoto: !e.imageOk,
  };
}

/** « ZD » pour Zoé Dupont ; « Z » sans nom ; « ? » sans rien. */
export function initiales(prenom: string, nom: string): string {
  const p = prenom.trim().charAt(0);
  const n = nom.trim().charAt(0);
  const i = `${p}${n}`.toUpperCase();
  return i || '?';
}

/**
 * Le groupe en deux parts : les enfants qui ont quelque chose à signaler, avec
 * leurs lignes, et les autres. Sans essentiels lus, tout le monde est « sans ».
 */
export function partagerLeGroupe<E extends { registrationId: string }>(
  enfants: readonly E[],
  essentiels: ReadonlyMap<string, EssentielsDeLEnfant> | null,
): { aSignaler: { enfant: E; lignes: LigneASavoir[] }[]; sansRien: E[] } {
  const aSignaler: { enfant: E; lignes: LigneASavoir[] }[] = [];
  const sansRien: E[] = [];
  for (const enfant of enfants) {
    const e = essentiels?.get(enfant.registrationId);
    const lignes = e ? lignesASavoir(e) : [];
    if (lignes.length > 0) aSignaler.push({ enfant, lignes });
    else sansRien.push(enfant);
  }
  return { aSignaler, sansRien };
}
